// ====================================================
// src/components/block/liveStreamManagment/videoEffects/AvatarModelAddModal.tsx
//
// Fenêtre d'ajout d'un modèle 3D (.glb) à l'effet Avatar — ouverte par la tuile "+" de
// chaque famille (Visage / Mains / Décoration). Le fichier part dans le workingSpace du
// créateur (cf. avatarModelStore.uploadAvatarModel). La famille est celle de la tuile
// cliquée ; seule une décoration demande en plus son point d'ancrage.
// ====================================================

import React, { useState } from 'react';
import { AvatarAnchorKind } from './AvatarModels';
import { AvatarModelFamily, uploadAvatarModel } from './avatarModelStore';

const FAMILY_LABELS: Record<AvatarModelFamily, string> = {
  face: 'Visage', hand: 'Mains', decoration: 'Décoration',
};

const DECORATION_ANCHORS: { id: AvatarAnchorKind; label: string }[] = [
  { id: 'face',      label: 'Visage (entre les sourcils)' },
  { id: 'eyes',      label: 'Yeux (lunettes)' },
  { id: 'aboveHead', label: 'Au-dessus de la tête (chapeau, couronne)' },
  { id: 'hand',      label: 'Main (bague, bracelet)' },
];

const fieldStyle: React.CSSProperties = {
  width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #475569',
  background: '#0f172a', color: '#e2e8f0', fontSize: 13,
};

export const AvatarModelAddModal: React.FC<{
  family: AvatarModelFamily;
  /** Pour une main : de quel côté on ajoute (tuile « + » de la main gauche ou droite). */
  side?: 'left' | 'right';
  onClose: () => void;
}> = ({ family, side, onClose }) => {
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState('');
  const [anchor, setAnchor] = useState<AvatarAnchorKind>('face');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!file || !label.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await uploadAvatarModel(file, label.trim(), family, anchor, side);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={() => { if (!busy) onClose(); }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: 360, maxWidth: '90vw', background: '#1e293b', border: '1px solid #475569', borderRadius: 10, padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <div style={{ fontSize: 15, fontWeight: 600, color: '#e2e8f0' }}>Ajouter un modèle — {family === 'hand' && side ? (side === 'left' ? 'Main gauche' : 'Main droite') : FAMILY_LABELS[family]}</div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#94a3b8' }}>
          Fichier .glb (100 Mo max)
          <input
            type="file" accept=".glb,model/gltf-binary"
            onChange={e => {
              const f = e.target.files?.[0] ?? null;
              setFile(f);
              if (f && !label) setLabel(f.name.replace(/\.glb$/i, '').slice(0, 60));
            }}
            style={{ color: '#e2e8f0', fontSize: 12 }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#94a3b8' }}>
          Nom
          <input type="text" value={label} maxLength={60} onChange={e => setLabel(e.target.value)} style={fieldStyle} />
        </label>

        {family === 'decoration' && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#94a3b8' }}>
            Ancrage
            <select value={anchor} onChange={e => setAnchor(e.target.value as AvatarAnchorKind)} style={fieldStyle}>
              {DECORATION_ANCHORS.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
          </label>
        )}

        {error && <div style={{ fontSize: 12, color: '#f87171' }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" disabled={busy} onClick={onClose}
            style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #475569', background: 'transparent', color: '#cbd5e1', cursor: 'pointer' }}>
            Annuler
          </button>
          <button type="button" disabled={busy || !file || !label.trim()} onClick={submit}
            style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#0ea5e9', color: '#fff', cursor: busy || !file || !label.trim() ? 'default' : 'pointer', opacity: busy || !file || !label.trim() ? 0.5 : 1 }}>
            {busy ? 'Envoi…' : 'Ajouter'}
          </button>
        </div>
      </div>
    </div>
  );
};
